import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fmtINR } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Loader2, LogOut, Download, FileCheck, UserMinus } from "lucide-react";
import { downloadLetterPdf } from "@/lib/pdf";
import { toast } from "sonner";

const DUES_FIELDS = [
  ["notice_shortfall", "Notice Shortfall"],
  ["asset_recovery", "Asset Recovery"],
  ["advances", "Salary Advances"],
  ["other", "Other Recoveries"],
];
const duesTotal = (items) => DUES_FIELDS.reduce((s, [k]) => s + (Number((items || {})[k]) || 0), 0);

function RelievingLetter({ rec }) {
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="print-area bg-white text-slate-900 rounded-xl border border-slate-200 p-8" data-testid="relieving-letter">
      <div className="flex items-center gap-3 border-b-2 border-slate-900 pb-4">
        <div className="w-11 h-11 rounded-lg bg-white border border-slate-200 overflow-hidden flex items-center justify-center"><img src="/logo.jpg" alt="Skanovix logo" className="w-full h-full object-cover" /></div>
        <div>
          <p className="font-heading font-extrabold tracking-tight">Skanovix Info Tech Solutions</p>
          <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">Relieving & Experience Certificate</p>
        </div>
      </div>
      <p className="text-right text-xs text-slate-500 mt-3">Date: {today}</p>
      <h2 className="font-heading text-center text-lg font-extrabold mt-4 mb-4 uppercase">Relieving Letter</h2>
      <p className="text-sm leading-relaxed text-slate-700">
        This is to certify that <span className="font-semibold">{rec.employee_name}</span> (Employee ID:{" "}
        <span className="font-mono">{rec.employee_id}</span>) was employed with Skanovix Info Tech Solutions as{" "}
        <span className="font-semibold">{rec.designation}</span> in the {rec.department} department from{" "}
        <span className="font-semibold">{rec.doj || "—"}</span> to{" "}
        <span className="font-semibold">{rec.last_working_day}</span>.
      </p>
      <p className="text-sm leading-relaxed text-slate-700 mt-3">
        During the tenure of approximately <span className="font-semibold">{rec.years_served} years</span>, the
        employee's conduct and performance were found to be satisfactory. All company dues and clearances have been
        settled and the employee is relieved of all duties with effect from {rec.last_working_day}.
      </p>
      <div className="mt-5 text-sm border border-slate-200 rounded-lg overflow-hidden" data-testid="relieving-settlement-block">
        <div className="bg-slate-900 text-white px-4 py-2 font-semibold flex justify-between"><span>Full &amp; Final Settlement</span><span>Amount</span></div>
        <div className="flex justify-between px-4 py-2 border-b border-slate-100"><span className="text-slate-600">Leave Encashment</span><span className="font-mono">{fmtINR(rec.leave_encashment)}</span></div>
        {DUES_FIELDS.filter(([k]) => (rec.dues_items || {})[k] > 0).map(([k, label]) => (
          <div key={k} className="flex justify-between px-4 py-2 border-b border-slate-100"><span className="text-slate-600">Less: {label}</span><span className="font-mono">{fmtINR(rec.dues_items[k])}</span></div>
        ))}
        <div className="flex justify-between px-4 py-2 font-semibold bg-slate-50"><span>Net Final Settlement Payable</span><span className="font-mono text-emerald-700">{fmtINR(rec.final_settlement ?? (rec.leave_encashment - (rec.dues || 0)))}</span></div>
      </div>
      <p className="text-sm leading-relaxed text-slate-700 mt-4">We wish {rec.employee_name} all the best in future endeavours.</p>
      <div className="mt-10 text-sm">
        <div className="w-40 border-b border-slate-400 mb-1" />
        <p className="font-semibold">Head — Human Resources</p>
        <p className="text-slate-500 text-xs">Skanovix Info Tech Solutions</p>
      </div>
    </div>
  );
}

export default function Relieving() {
  const { role } = useOutletContext();
  const qc = useQueryClient();
  const isHR = role === "hr";
  const [open, setOpen] = useState(false);
  const [letter, setLetter] = useState(null);
  const [form, setForm] = useState({ employee_id: "", resignation_date: "", last_working_day: "", reason: "", notice_days: 90, dues_items: { notice_shortfall: 0, asset_recovery: 0, advances: 0, other: 0 } });

  const { data: relievings = [], isLoading } = useQuery({
    queryKey: ["relieving"], queryFn: async () => (await api.get("/relieving")).data,
  });
  const { data: employees = [] } = useQuery({
    queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data, enabled: isHR,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/relieving", form)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["relieving"] }); qc.invalidateQueries({ queryKey: ["employees"] }); setOpen(false); toast.success("Exit process initiated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const updateMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/relieving/${id}`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["relieving"] }); qc.invalidateQueries({ queryKey: ["employees"] }); toast.success("Updated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDue = (k, v) => setForm((f) => ({ ...f, dues_items: { ...f.dues_items, [k]: v } }));

  return (
    <div>
      <PageHeader title="Relieving & Exit Management" subtitle="Handle resignations, no-dues clearance and relieving letters.">
        {isHR && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="relieving-initiate-button" className="rounded-lg font-semibold active:scale-[0.98]">
                <UserMinus className="w-4 h-4 mr-1.5" /> Initiate Exit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Initiate Employee Exit</DialogTitle><DialogDescription>Start the relieving workflow and no-dues clearance.</DialogDescription></DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5"><Label>Employee</Label>
                  <Select value={form.employee_id} onValueChange={(v) => set("employee_id", v)}>
                    <SelectTrigger data-testid="relieving-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>{employees.filter((e) => e.status !== "Relieved").map((e) => <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_id})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Resignation Date</Label><Input type="date" value={form.resignation_date} onChange={(e) => set("resignation_date", e.target.value)} /></div>
                  <div className="space-y-1.5"><Label>Last Working Day</Label><Input type="date" value={form.last_working_day} onChange={(e) => set("last_working_day", e.target.value)} /></div>
                </div>
                <div className="space-y-1.5"><Label>Notice Period (days)</Label><Input type="number" value={form.notice_days} onChange={(e) => set("notice_days", Number(e.target.value))} data-testid="relieving-notice-input" /></div>
                <div className="space-y-1.5">
                  <Label>Dues &amp; Recoveries (₹)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {DUES_FIELDS.map(([k, label]) => (
                      <div key={k} className="space-y-1">
                        <span className="text-[11px] text-muted-foreground">{label}</span>
                        <Input type="number" min="0" value={form.dues_items[k]} onChange={(e) => setDue(k, Number(e.target.value))} data-testid={`relieving-dues-${k}`} placeholder="0" />
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Total dues: ₹{duesTotal(form.dues_items).toLocaleString("en-IN")}</p>
                </div>
                <div className="space-y-1.5"><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Reason for leaving" /></div>
              </div>
              <DialogFooter>
                <Button onClick={() => createMut.mutate()} disabled={createMut.isPending || !form.employee_id || !form.last_working_day} data-testid="relieving-save-button" className="rounded-lg">
                  {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Initiate"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : relievings.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border border-dashed border-border rounded-xl">
          <LogOut className="w-8 h-8 mx-auto mb-3 opacity-50" />
          No exit processes yet.
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {relievings.map((r) => {
            const allClear = r.clearance.it_assets && r.clearance.finance && r.clearance.hr;
            return (
              <Card key={r.id} className="p-5 border border-border rounded-xl" data-testid={`relieving-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{r.employee_name}</p>
                    <p className="text-xs text-muted-foreground">{r.designation} • {r.employee_id}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
                  <div><p className="text-muted-foreground">Last Working Day</p><p className="font-mono font-medium">{r.last_working_day}</p></div>
                  <div><p className="text-muted-foreground">Tenure</p><p className="font-mono font-medium">{r.years_served} yrs</p></div>
                  <div><p className="text-muted-foreground">Leave Encashment</p><p className="font-mono font-medium text-accent">{fmtINR(r.leave_encashment)}</p></div>
                  <div><p className="text-muted-foreground">Net Final Settlement</p><p className="font-mono font-semibold text-emerald-600" data-testid={`settlement-net-${r.id}`}>{fmtINR(r.final_settlement ?? (r.leave_encashment - (r.dues || 0)))}</p></div>
                </div>
                {isHR && r.status !== "Relieved" && (
                  <div className="mt-3 border border-dashed border-border rounded-lg p-2.5">
                    <p className="text-[11px] text-muted-foreground mb-1.5">Dues &amp; recoveries (auto-saves)</p>
                    <div className="grid grid-cols-2 gap-2">
                      {DUES_FIELDS.map(([k, label]) => (
                        <div key={k} className="space-y-0.5">
                          <span className="text-[10px] text-muted-foreground">{label}</span>
                          <Input type="number" min="0" defaultValue={(r.dues_items || {})[k] || 0} className="h-8" data-testid={`dues-input-${k}-${r.id}`}
                            onBlur={(e) => { const v = Number(e.target.value); const cur = r.dues_items || {}; if (v !== (cur[k] || 0)) updateMut.mutate({ id: r.id, body: { dues_items: { ...cur, [k]: v } } }); }} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 border-t border-border pt-3">
                  <p className="text-xs font-semibold mb-2">No-Dues Clearance</p>
                  <div className="space-y-2">
                    {[["it_assets", "IT Assets"], ["finance", "Finance"], ["hr", "HR Clearance"]].map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={r.clearance[k]} disabled={!isHR}
                          onCheckedChange={(v) => updateMut.mutate({ id: r.id, body: { [k]: !!v } })}
                          data-testid={`clearance-${k}-${r.id}`}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                </div>

                {isHR && (
                  <div className="flex gap-2 mt-4">
                    {r.status !== "Relieved" && (
                      <Button size="sm" variant="secondary" className="rounded-lg flex-1" disabled={!allClear || updateMut.isPending}
                        onClick={() => updateMut.mutate({ id: r.id, body: { status: "Relieved" } })} data-testid={`relieve-button-${r.id}`}>
                        <FileCheck className="w-4 h-4 mr-1" /> Mark Relieved
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => setLetter(r)} data-testid={`relieving-letter-button-${r.id}`}>
                      <FileCheck className="w-4 h-4 mr-1" /> Letter
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!letter} onOpenChange={(o) => !o && setLetter(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading flex items-center justify-between">
              Relieving Letter
              <Button size="sm" variant="outline" className="rounded-lg mr-4" onClick={() => downloadLetterPdf(`Relieving_${(letter?.employee_name || "Letter").replace(/\s+/g, "_")}.pdf`)} data-testid="print-relieving-button">
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">Relieving and experience certificate preview.</DialogDescription>
          </DialogHeader>
          {letter && <RelievingLetter rec={letter} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
